import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useState, useEffect, useCallback } from 'react';

export interface DateTimeData {
    date: string;
    time: string;
}

interface DateTimePickerProps {
    onDateTimeChange: (data: DateTimeData) => void;
    dateTime?: string;
    title?: string;
    isRequired?: boolean;
}

export default function DateTimePicker({
    onDateTimeChange,
    dateTime = "",
    title = "Date and Time",
    isRequired = false
}: DateTimePickerProps) {

    const parseTime = (timeString: string) => {
        if (!timeString) return { hours: "", minutes: "" };

        const timePart = timeString.includes('T')
            ? timeString.split('T')[1]
            : timeString;

        const [hours, minutes] = timePart.split(':');

        return {
            hours: hours || "",
            minutes: minutes || ""
        };
    };

    const [selectedDate, setSelectedDate] = useState<Date | null>(
        dateTime ? new Date(dateTime) : null
    );
    const [timeValue, setTimeValue] = useState(() => parseTime(dateTime));


    useEffect(() => {
        const date = dateTime ? new Date(dateTime) : null;
        const time = parseTime(dateTime);
        setSelectedDate(date);
        setTimeValue(time);
    }, [dateTime]);


    const handleDateChange = useCallback((date: Date | null | undefined) => {
        const safeDate = date || null;
        setSelectedDate(safeDate);
        const dateString = safeDate ? safeDate.toLocaleDateString('sv-SE') : "";
        const timeString = timeValue.hours && timeValue.minutes ? `${timeValue.hours}:${timeValue.minutes}` : "";
        onDateTimeChange({ date: dateString, time: timeString });
    }, [timeValue, onDateTimeChange]);

    const handleTimeChange = useCallback((newHours: string, newMinutes: string) => {
        const newTimeValue = { hours: newHours, minutes: newMinutes };
        setTimeValue(newTimeValue);
        const dateString = selectedDate ? selectedDate.toLocaleDateString('sv-SE') : "";
        const timeString = newHours && newMinutes ? `${newHours}:${newMinutes}` : "";
        onDateTimeChange({ date: dateString, time: timeString });
    }, [selectedDate, onDateTimeChange]); const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers

        if (value === '') {
            handleTimeChange('', timeValue.minutes);
            return;
        }

        if (value.length > 2) {
            value = value.slice(0, 2);
        }

        const numValue = parseInt(value);
        if (numValue > 23) {
            value = '23';
        }

        handleTimeChange(value, timeValue.minutes);
    };

    const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers

        if (value === '') {
            handleTimeChange(timeValue.hours, '');
            return;
        }

        if (value.length > 2) {
            value = value.slice(0, 2);
        }

        const numValue = parseInt(value);
        if (numValue > 59) {
            value = '59';
        }

        handleTimeChange(timeValue.hours, value);
    };

    const handleHoursBlur = () => {
        if (timeValue.hours && timeValue.hours.length === 1) {
            handleTimeChange(timeValue.hours.padStart(2, '0'), timeValue.minutes);
        }
    };

    const handleMinutesBlur = () => {
        if (timeValue.minutes && timeValue.minutes.length === 1) {
            handleTimeChange(timeValue.hours, timeValue.minutes.padStart(2, '0'));
        }
    };

    const blueOutlineClass = isRequired ? "border-oi-button-blue" : "border-gray-300 dark:border-gray-600";

    const borderColor = isRequired ? '#066CEA' : '#d1d5db';

    return (
        <div className="">
            <div className="flex flex-col p-4 gap-2">
                <h6>{title}</h6>
                <div className="flex flex-wrap gap-2">
                    <DatePicker
                        className={`min-w-[140px] flex-1 ${blueOutlineClass}`}
                        placeholder={new Date().toLocaleDateString("sv-se")}
                        value={selectedDate}
                        onSelectDate={handleDateChange}
                        style={{ height: "48px", borderRadius: "0px", borderColor: borderColor, backgroundColor: "transparent" }}
                    />
                    <div className="flex gap-2 items-center">
                        <input
                            type="text"
                            placeholder="h"
                            value={timeValue.hours}
                            onChange={handleHoursChange}
                            onBlur={handleHoursBlur}
                            maxLength={2}
                            className={`w-14 h-12 p-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border ${blueOutlineClass} bg-primary-light dark:bg-primary-dark`}
                        />
                        <input
                            type="text"
                            placeholder="min"
                            value={timeValue.minutes}
                            onChange={handleMinutesChange}
                            onBlur={handleMinutesBlur}
                            maxLength={2}
                            className={`w-14 h-12 p-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border ${blueOutlineClass} bg-primary-light dark:bg-primary-dark`}
                        />
                    </div>
                </div>
                {isRequired && (!selectedDate || !timeValue.hours || !timeValue.minutes) && (
                    <div className="text-left pt-4 text-gray-500">
                        <p className="text-bad-red">*Mandatory field </p>
                    </div>
                )}
            </div>
        </div>

    );
}